import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStatus } from '../hooks/useAuthStatus';
import { deckService } from '../services/deckService';
import { DeckWithStats, DeckStats, getFileTypeInfo, formatFileSize, formatDate } from '../types/deck';
import { PageHeader } from '../components/common';
import { StatCard } from '../components/dashboard';
import UploadDeckModal from '../components/decks/UploadDeckModal';
import ShareLinkModal from '../components/decks/ShareLinkModal';
import DeleteConfirmModal from '../components/decks/DeleteConfirmModal';
import PaymentSuccessModal from '../components/common/PaymentSuccessModal';
import { usePaymentSuccess } from '../hooks/usePaymentSuccess';
import {
  FiFile,
  FiEye,
  FiUsers,
  FiLink,
  FiPlus,
  FiTrash2,
  FiShare2,
} from 'react-icons/fi';

// Type cast icons for React 18 compatibility
const FileIcon = FiFile as React.FC<React.SVGProps<SVGSVGElement>>;
const EyeIcon = FiEye as React.FC<React.SVGProps<SVGSVGElement>>;
const UsersIcon = FiUsers as React.FC<React.SVGProps<SVGSVGElement>>;
const LinkIcon = FiLink as React.FC<React.SVGProps<SVGSVGElement>>;
const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;
const ShareIcon = FiShare2 as React.FC<React.SVGProps<SVGSVGElement>>;

const Decks: React.FC = () => {
  const navigate = useNavigate();
  const { isLoading: authLoading, error: authError, canAccessApp } = useAuthStatus();
  const { showModal: showPaymentSuccessModal, planTier: paymentPlanTier, closeModal: closePaymentSuccessModal } = usePaymentSuccess();

  // Data state
  const [decks, setDecks] = useState<DeckWithStats[]>([]);
  const [stats, setStats] = useState<DeckStats>({
    total_decks: 0,
    total_views: 0,
    unique_viewers: 0,
    active_links: 0,
  });

  // Loading and error state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [sharingDeck, setSharingDeck] = useState<DeckWithStats | null>(null);
  const [deletingDeck, setDeletingDeck] = useState<DeckWithStats | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [decksData, statsData] = await Promise.all([
        deckService.getDecks(),
        deckService.getStats(),
      ]);
      setDecks(decksData);
      setStats(statsData);
    } catch (err: any) {
      console.error('Failed to load decks:', err);
      setError(err?.message || 'Failed to load decks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || authError || !canAccessApp) return;
    loadData();
  }, [authLoading, authError, canAccessApp, loadData]);

  // Handlers
  const handleUploadDeck = async (file: File) => {
    try {
      const newDeck = await deckService.uploadDeck(file);
      await loadData();
      // Navigate to the new deck
      navigate(`/decks/${newDeck.id}`);
    } catch (err: any) {
      console.error('Failed to upload deck:', err);
      setError(err?.message || 'Failed to upload deck');
      throw err;
    }
  };

  const handleDeleteDeck = async () => {
    if (!deletingDeck) return;
    setIsDeleting(true);
    try {
      await deckService.deleteDeck(deletingDeck.id);
      setDeletingDeck(null);
      await loadData();
    } catch (err: any) {
      console.error('Failed to delete deck:', err);
      setError(err?.message || 'Failed to delete deck. Please try again.');
      setDeletingDeck(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCardClick = (deck: DeckWithStats) => {
    navigate(`/decks/${deck.id}`);
  };

  const handleDeleteClick = (e: React.MouseEvent, deck: DeckWithStats) => {
    e.stopPropagation();
    setDeletingDeck(deck);
  };

  const handleShareClick = (e: React.MouseEvent, deck: DeckWithStats) => {
    e.stopPropagation();
    setSharingDeck(deck);
  };

  // Early returns for auth states
  if (authError) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4 text-red-600">Authentication Error</h1>
        <p className="text-red-500">{authError}</p>
      </div>
    );
  }

  if (!canAccessApp) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4">Access Denied</h1>
        <p>You don&apos;t have access to this page.</p>
      </div>
    );
  }

  return (
    <>
      <div className="su-deck-page">
        {/* Header */}
        <PageHeader
          title="Pitch Decks"
          subtitle="Share and track your pitch deck presentations"
          action={
            <button
              className="su-btn-primary"
              onClick={() => setShowUploadModal(true)}
            >
              <PlusIcon />
              <span>UPLOAD DECK</span>
            </button>
          }
        />

        {/* Stats */}
        <div className="su-deck-stats-grid">
          <StatCard
            icon={<FileIcon />}
            label="Total Decks"
            value={loading ? '—' : stats.total_decks}
            colorTheme="purple"
            isLoading={loading}
          />
          <StatCard
            icon={<EyeIcon />}
            label="Total Views"
            value={loading ? '—' : stats.total_views}
            colorTheme="green"
            isLoading={loading}
          />
          <StatCard
            icon={<UsersIcon />}
            label="Unique Viewers"
            value={loading ? '—' : stats.unique_viewers}
            colorTheme="purple"
            isLoading={loading}
          />
          <StatCard
            icon={<LinkIcon />}
            label="Active Links"
            value={loading ? '—' : stats.active_links}
            colorTheme="green"
            isLoading={loading}
          />
        </div>

        {/* Error state */}
        {error && (
          <div className="su-deck-error">
            {error}
            <button
              className="su-btn-secondary"
              style={{ marginLeft: '16px' }}
              onClick={loadData}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="su-deck-grid">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="su-deck-card animate-pulse"
                style={{ height: '160px' }}
              >
                <div className="h-5 w-32 bg-gray-800 rounded mb-3" />
                <div className="h-4 w-full bg-gray-800 rounded mb-4" />
                <div className="flex gap-4">
                  <div className="h-3 w-16 bg-gray-800 rounded" />
                  <div className="h-3 w-20 bg-gray-800 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && decks.length === 0 && (
          <div className="su-deck-empty">
            <div className="su-deck-empty-icon">
              <FileIcon style={{ width: 48, height: 48 }} />
            </div>
            <h3 className="su-deck-empty-title">No pitch decks yet</h3>
            <p className="su-deck-empty-subtitle">
              Upload your pitch deck to share with investors. Track views, get notified when someone views your deck.
            </p>
            <button
              className="su-btn-primary"
              onClick={() => setShowUploadModal(true)}
            >
              <PlusIcon />
              <span>UPLOAD YOUR FIRST DECK</span>
            </button>
          </div>
        )}

        {/* Pro tip banner */}
        {!loading && decks.length === 0 && (
          <div className="su-protip-banner">
            <span className="su-protip-label">Pro Tip:</span>
            Get email notifications when investors view your deck. Track engagement and follow up at the right time.
          </div>
        )}

        {/* Decks grid */}
        {!loading && decks.length > 0 && (
          <div className="su-deck-grid">
            {decks.map((deck) => {
              const fileTypeInfo = getFileTypeInfo(deck.file_type);
              return (
                <div
                  key={deck.id}
                  className="su-deck-card"
                  onClick={() => handleCardClick(deck)}
                >
                  <div className="su-deck-card-header">
                    <div className="su-deck-card-title-row">
                      <span
                        className="su-deck-file-badge"
                        style={{ backgroundColor: fileTypeInfo.color }}
                      >
                        {fileTypeInfo.label}
                      </span>
                      <span className="su-deck-card-name">{deck.name}</span>
                    </div>
                    <div className="su-deck-card-actions">
                      <button
                        type="button"
                        className="su-deck-action-btn share"
                        onClick={(e) => handleShareClick(e, deck)}
                        title="Share deck"
                        aria-label="Share deck"
                      >
                        <ShareIcon width={14} height={14} />
                      </button>
                      <button
                        type="button"
                        className="su-deck-action-btn delete"
                        onClick={(e) => handleDeleteClick(e, deck)}
                        title="Delete deck"
                        aria-label="Delete deck"
                      >
                        <TrashIcon width={14} height={14} />
                      </button>
                    </div>
                  </div>
                  {deck.description && (
                    <p className="su-deck-card-desc">{deck.description}</p>
                  )}
                  <div className="su-deck-card-meta">
                    <span className="su-deck-card-meta-item">
                      <EyeIcon width={12} height={12} />
                      {deck.view_count} views
                    </span>
                    <span className="su-deck-card-meta-item">
                      <LinkIcon width={12} height={12} />
                      {deck.share_link_count} links
                    </span>
                    <span className="su-deck-card-meta-item">
                      {formatFileSize(deck.file_size)}
                    </span>
                    <span className="su-deck-card-meta-item">
                      {formatDate(deck.created_at)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      {showUploadModal && (
        <UploadDeckModal
          onClose={() => setShowUploadModal(false)}
          onSubmit={handleUploadDeck}
        />
      )}

      {sharingDeck && (
        <ShareLinkModal
          deck={sharingDeck}
          onClose={() => setSharingDeck(null)}
        />
      )}

      {deletingDeck && (
        <DeleteConfirmModal
          title="Delete Deck"
          message={`Are you sure you want to delete "${deletingDeck.name}"? This will permanently delete the deck, all share links, and analytics. This action cannot be undone.`}
          onCancel={() => setDeletingDeck(null)}
          onConfirm={handleDeleteDeck}
          isDeleting={isDeleting}
        />
      )}

      {/* Payment Success Modal */}
      <PaymentSuccessModal
        isOpen={showPaymentSuccessModal}
        onClose={closePaymentSuccessModal}
        planTier={paymentPlanTier}
      />
    </>
  );
};

export default Decks;
